/**
 * `InstagramCommentList` — the `/inbox` **Instagram comments** tab
 * (2026-08-07).
 *
 * The sibling of `ChatList`, and deliberately NOT a reuse of it: a comment is
 * not a conversation. There is no thread to open, no 24-hour window, no sender
 * to resolve to a member — just a public remark under one of the studio's own
 * posts, and a reply that is itself public and permanent.
 *
 * Three things this component exists to get right:
 *
 *   1. **`handle unavailable` is rendered, never invented.** Meta withheld the
 *      comment author by SILENT FIELD OMISSION until `instagram_manage_comments`
 *      was granted 2026-08-07 — HTTP 200 with the key simply absent. Authors
 *      resolve now, so an authorless row is a REGRESSION signal, and the banner
 *      below says so out loud rather than letting an anonymous board look
 *      normal.
 *   2. **The permalink is the durable link; the thumbnail is a cache.**
 *      Instagram CDN URLs are signed and expire within days, so the tile falls
 *      back to a plain "View post" link rather than rendering a broken image.
 *   3. **Replying is a deliberate act.** The composer is per-comment and posts
 *      through `reply_instagram_comment`, whose actor matrix is staff + CLI
 *      only. The copy says "public" because the operator is about to say
 *      something under their own post that they cannot edit afterwards.
 */

import { useState, type JSX } from "react";
import { useDispatch } from "../lib/use-dispatch";
import type { InstagramCommentRow, InstagramCommentsResponse } from "../lib/api";

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function kindLabel(kind: InstagramCommentRow["kind"]): string {
  return kind === "mention"
    ? "mention"
    : kind === "live_comment"
      ? "live comment"
      : "comment";
}

function CommentReplyComposer({
  comment,
  onReplied,
}: {
  readonly comment: InstagramCommentRow;
  readonly onReplied: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>("");
  const dispatch = useDispatch<
    { readonly engagement_event_id: string; readonly body: string },
    { readonly reply_comment_id: string }
  >("reply_instagram_comment");

  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !dispatch.loading;

  const send = async (): Promise<void> => {
    if (!canSend) return;
    const r = await dispatch.run({
      engagement_event_id: comment.id,
      body: trimmed,
    });
    if (r.ok) {
      setDraft("");
      onReplied();
    }
  };

  return (
    <div className="ig-comment-composer">
      <textarea
        className="dc-textarea"
        rows={2}
        value={draft}
        placeholder="Write a public reply…"
        onChange={(e) => setDraft(e.target.value)}
        // Meta's comment cap. The chokepoint enforces it too (JSON Schema is
        // the SOLE enforcement layer); this only stops the operator typing
        // past it and losing the tail.
        maxLength={2200}
      />
      <div className="ig-comment-composer-actions">
        <button
          type="button"
          className="dc-btn"
          disabled={!canSend}
          onClick={() => void send()}
        >
          {dispatch.loading ? "Replying…" : "Reply publicly"}
        </button>
        <span className="dc-muted">
          Posts under the studio&apos;s post. Public and permanent — Instagram
          has no edit.
        </span>
      </div>
      {dispatch.error !== null ? (
        <p className="dc-error">
          Reply failed: {dispatch.error.code} — {dispatch.error.message}
        </p>
      ) : null}
    </div>
  );
}

export function InstagramCommentList({
  board,
  onReplied,
}: {
  readonly board: InstagramCommentsResponse | null;
  readonly onReplied?: () => void;
}): JSX.Element {
  if (board === null) {
    return (
      <p className="empty">
        Instagram comments could not be loaded. The mail pipeline above is
        unaffected.
      </p>
    );
  }
  if (board.comments.length === 0) {
    return (
      <p className="empty">
        No Instagram comments yet. They arrive on the `comments` webhook and
        appear here within about a minute.
      </p>
    );
  }

  return (
    <section className="pr-stage">
      {/* A wholly-authorless board is the silent-omission failure mode, not a
          quiet day — say so rather than letting it look normal. */}
      {board.counts.authorless > 0 ? (
        <p
          className={
            board.counts.authorless === board.counts.total
              ? "dc-error"
              : "subtle"
          }
        >
          {board.counts.authorless} of {board.counts.total} comment
          {board.counts.total === 1 ? "" : "s"} arrived with no readable author
          handle
          {board.counts.authorless === board.counts.total
            ? " — that is every one of them, which usually means the instagram_manage_comments scope has lapsed."
            : "."}
        </p>
      ) : null}

      <div className="table-scroll-x">
        <table className="pr-people-table">
          <thead>
            <tr>
              <th>Who</th>
              <th>Comment</th>
              <th>Post</th>
              <th>When</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {board.comments.map((cm) => (
              <tr key={cm.id}>
                <td>
                  {cm.author_username !== null ? (
                    <a
                      href={`https://www.instagram.com/${cm.author_username}/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      @{cm.author_username}
                    </a>
                  ) : (
                    <span className="dc-muted" title="Meta did not return the author for this comment">
                      handle unavailable
                    </span>
                  )}
                  <div className="dc-muted">{kindLabel(cm.kind)}</div>
                </td>
                <td>
                  {cm.text ?? <span className="dc-muted">(no text)</span>}
                  {cm.replied_at === null && cm.kind !== "mention" ? (
                    <CommentReplyComposer
                      comment={cm}
                      onReplied={() => onReplied?.()}
                    />
                  ) : null}
                </td>
                <td>
                  {cm.media_permalink !== null ? (
                    <a
                      href={cm.media_permalink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View post
                    </a>
                  ) : (
                    <span className="dc-muted">—</span>
                  )}
                </td>
                <td title={cm.occurred_at}>
                  {relativeTime(cm.occurred_at)}
                  {/* The ingest clock, not the comment's own time — enrichment
                      has not run (or failed). Marked so nobody reads an
                      approximate timestamp as exact. */}
                  {cm.time_is_approximate ? (
                    <span className="dc-muted" title="Ingest time — the comment's own timestamp has not been read yet">
                      {" "}
                      ~
                    </span>
                  ) : null}
                </td>
                <td>
                  {cm.replied_at !== null ? (
                    <span className="dc-muted">replied</span>
                  ) : cm.kind === "mention" ? (
                    <span className="dc-muted">no reply target</span>
                  ) : (
                    <span>awaiting reply</span>
                  )}
                  {cm.enrichment_error !== null ? (
                    <div className="dc-muted" title={cm.enrichment_error}>
                      enrichment failed
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
